import type { QueryClient } from '@tanstack/react-query';

import type { TaskTrackingSummary } from '@/lib/types';

/**
 * Invalidate every query a reverse-sync (pull from Google Ads) can move, in one place (#5745).
 *
 * A reverse sync can change campaign basics (budget/state/bidding), keywords, ads/headlines,
 * labels, excluded locations, negative-keyword-list membership, account-tier assets, the
 * conversion registry, and the derived action logs / recommendations. The prior inline block in
 * useReverseSync missed five of those roots, so those views showed stale data after a sync.
 *
 * TanStack invalidation is element-wise prefix matching, so a root key (e.g. ['ad-labels', id])
 * matches every longer key beneath it. Two per-key gotchas are load-bearing:
 *   - ['actionLogs'] (camelCase) is a DISTINCT query from ['action-logs', slug] (hyphen) — both fire.
 *   - The task-tracking poller key is ['campaignTaskTracking', adCreation, campaignId]; a
 *     [..., campaignId] filter does NOT match it (position 1 is adCreation), so only the root works.
 *
 * accountId is optional: when omitted (the durable-layout caller) the account-tier keys are
 * invalidated at the root so every mounted account view refreshes — a strict superset of the old
 * behaviour. Pass an id to scope the account-tier invalidation to a single account.
 */
export const invalidateCampaignReverseSync = (
  queryClient: QueryClient,
  campaignId: number,
  slug: string,
  accountId?: string | null,
) => {
  // Campaign-scoped views.
  queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  queryClient.invalidateQueries({ queryKey: ['campaign'] });
  queryClient.invalidateQueries({ queryKey: ['campaignTasks', campaignId] });
  queryClient.invalidateQueries({ queryKey: ['campaignRecommendations', campaignId] });
  queryClient.invalidateQueries({ queryKey: ['campaign-excluded-locations', campaignId] });
  queryClient.invalidateQueries({ queryKey: ['ad-labels', campaignId] });
  // Root-only: position 1 is adCreation, so [..., campaignId] would not match (see docstring).
  queryClient.invalidateQueries({ queryKey: ['campaignTaskTracking'] });

  // Activity feed — two distinct query families, both driven by a sync.
  queryClient.invalidateQueries({ queryKey: ['action-logs', slug] });
  queryClient.invalidateQueries({ queryKey: ['actionLogs'] });

  // Account-tier data the manual sync also pulls (sitelinks, callouts, IP exclusions, shared
  // negative lists, conversion registry). Root when the account is unknown; scoped when supplied.
  const accountScoped = (base: string) =>
    queryClient.invalidateQueries({ queryKey: accountId ? [base, accountId] : [base] });
  accountScoped('account-sitelinks');
  accountScoped('account-callouts');
  accountScoped('account-ip-exclusions');
  queryClient.invalidateQueries({ queryKey: ['negative-keyword-lists'] });
  // Singular root covers both the 4-part [...,'keywords'] list-detail and the 3-part
  // [...,'campaigns'] applied-campaigns queries.
  queryClient.invalidateQueries({ queryKey: ['negative-keyword-list'] });
  queryClient.invalidateQueries({ queryKey: ['conversion-actions'] });
};

/**
 * Decide whether a reverse-sync just completed, given the previous and current
 * `campaign_reverse_sync` task-tracking summaries (#5745).
 *
 * Returns false for the initial snapshot (`prev` null/undefined) so a page that opens onto an
 * already-cached "N completed" summary does not fire spuriously — the first observation only seeds
 * the baseline.
 *
 * Fires on EITHER:
 *   - an in_progress 1→0 edge (a foreground sync — manual button or auto-pull — finished), OR
 *   - a completed/total increment while in_progress is 0 (the nightly scheduled job writes a
 *     terminal COMPLETED row directly, so it never passes through in_progress > 0).
 *
 * The `in_progress === 0` guard on the increment branches prevents firing on the START of a
 * foreground sync (total ticks up as the IN_PROGRESS row is created).
 */
export function shouldInvalidateReverseSync(
  prev: TaskTrackingSummary | null | undefined,
  current: TaskTrackingSummary | null | undefined,
): boolean {
  if (!current) return false;
  if (!prev) return false;

  const inProgressFellToZero = prev.in_progress > 0 && current.in_progress === 0;
  const completedIncreased = current.completed > prev.completed && current.in_progress === 0;
  const totalIncreasedWhileIdle = current.total > prev.total && current.in_progress === 0;

  return inProgressFellToZero || completedIncreased || totalIncreasedWhileIdle;
}
